//
// Enforces an 8-hour hard session limit regardless of PingOne token renewal.
// oidc-client-ts's automaticSilentRenew continues to work transparently
// within this window. Once 8 hours elapse from the time of login, the user
// is signed out and redirected to the login page.
//
// How it works:
// - On first login, the login timestamp is written to sessionStorage.
// - On every page load/refresh, the remaining time is calculated from that
//   timestamp and a timer is set for the remainder.
// - sessionStorage is used (not localStorage) so the timer resets naturally
//   when the browser is closed.

import { useEffect, useRef } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";

const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const SESSION_START_KEY = "sessionStart";

export const useSessionTimeout = () => {
  const { isAuthenticated, signoutRedirect } = useOidcAuth();
  const timerRef = useRef(null);

  const signOut = () => {
    sessionStorage.removeItem(SESSION_START_KEY);

    // Clean up security-sensitive keys
    localStorage.removeItem("Name");
    localStorage.removeItem("Access");
    localStorage.setItem("sessionExpired", "true");

    // Save current path so user is returned here after re-login
    const currentPath = window.location.pathname;
    if (currentPath !== "/" && !currentPath.includes("bulkedit")) {
      localStorage.setItem("failedUrl", currentPath);
    }

    // PingOne logout — ends the AccessHub session entirely, and clears
    // oidc-client-ts's own sessionStorage cache via signoutRedirect.
    signoutRedirect({ post_logout_redirect_uri: `${window.location.origin}/` });
  };

  useEffect(() => {
    // Only start the timer when a user is actually logged in
    if (!isAuthenticated) return;

    // Record login time if this is a fresh login
    if (!sessionStorage.getItem(SESSION_START_KEY)) {
      sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
    }

    const sessionStart = parseInt(
      sessionStorage.getItem(SESSION_START_KEY),
      10,
    );
    const elapsed = Date.now() - sessionStart;
    const remaining = SESSION_DURATION_MS - elapsed;

    // Session already exceeded 8 hours (e.g. tab left open overnight)
    if (remaining <= 0) {
      signOut();
      return;
    }

    // Set timer for the remaining window
    timerRef.current = setTimeout(() => {
      signOut();
    }, remaining);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated]);
};
